import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterConsultationMutation } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { DEFAULT_COMMUNICATION_METHOD, userConsultationPayload, withDefaultCommunicationMethods } from 'features/consultations/consultationUtils';
import { userApi } from 'features/home/home.api';
import { Button } from 'shared/components/Button';
import { SelectInput, TextArea } from 'shared/components/Input';
import { Card, EmptyState, ErrorState, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { PatternChip } from 'shared/components/PatternBar';

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}`;
}

function optionLabel(options, value) {
  return (options || []).find((item) => item.value === value)?.label || value || '—';
}

export function NewConsultationPage() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const optionsQuery = useQuery({
    queryKey: queryKeys.consultationOptions,
    queryFn: () => userApi.consultationOptions(),
  });
  const resultsQuery = useQuery({
    queryKey: queryKeys.results(1),
    queryFn: () => userApi.results({ page: 1, per_page: 20 }),
  });
  const [form, setForm] = useState({
    goal: '',
    question: '',
    preferred_period: '',
    attempt_id: '',
    share_report_consent: false,
    communication_method: DEFAULT_COMMUNICATION_METHOD,
  });
  const [review, setReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const idempotencyKey = useMemo(() => createIdempotencyKey(), []);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.attempt_id) {
      return;
    }
    if (!review) {
      setReview(true);
      return;
    }
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = userConsultationPayload({
        goal: form.goal,
        question: form.question,
        preferred_period: form.preferred_period,
        attempt_id: Number(form.attempt_id),
        share_report_consent: form.share_report_consent,
        communication_method: form.communication_method || DEFAULT_COMMUNICATION_METHOD,
        idempotency_key: idempotencyKey,
      });
      const data = await userApi.createConsultation(payload);
      await invalidateAfterConsultationMutation(queryClient, data?.id);
      setCreated(data);
    } catch (err) {
      setError(humanizeApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  if (optionsQuery.isLoading) {
    return <Skeleton rows={7} />;
  }
  if (optionsQuery.isError) {
    return <ErrorState title={humanizeApiError(optionsQuery.error, t)} onRetry={() => optionsQuery.refetch()} retryLabel={t('actions.retry')} />;
  }
  if (resultsQuery.isError) {
    return <ErrorState title={humanizeApiError(resultsQuery.error, t)} onRetry={() => resultsQuery.refetch()} retryLabel={t('actions.retry')} />;
  }
  if (resultsQuery.isLoading) {
    return <Skeleton rows={7} />;
  }

  const options = optionsQuery.data || {};
  const communicationMethods = withDefaultCommunicationMethods(
    options.communication_methods,
    t('consultations.methodVideo'),
  );
  const attempts = resultsQuery.data?.items || [];
  const selectedAttempt = attempts.find((item) => String(item.attempt_id) === String(form.attempt_id));
  const canSubmit = Boolean(form.attempt_id);

  if (created) {
    return (
      <Card className="success-card">
        <PageMeta titleKey="meta.consultations" />
        <h1>{t('consultations.success')}</h1>
        <p data-testid="consultation-reference">{t('consultations.reference', { reference: created.reference })}</p>
        <Link className="btn btn-primary" to={paths.consultation(created.id)}>{t('actions.view')}</Link>
      </Card>
    );
  }

  if (attempts.length === 0) {
    return (
      <div className="form-page">
        <PageMeta titleKey="meta.consultations" />
        <h1>{t('consultations.newTitle')}</h1>
        <div data-testid="consultations-needs-result">
          <EmptyState
            title={t('consultations.needsResult')}
            action={<Link className="btn btn-primary" to={paths.assessmentIntro}>{t('actions.startAssessment')}</Link>}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="contact-layout">
      <form className="form-page form-page-wide" onSubmit={handleSubmit}>
        <PageMeta titleKey="meta.consultations" />
        <h1>{t('consultations.newTitle')}</h1>
        <p className="muted">{t('consultations.assignHint')}</p>
        <p className="muted">{t('consultations.noPayment')}</p>
        <SelectInput id="goal" label={t('consultations.goal')} value={form.goal} onChange={(event) => update('goal', event.target.value)} required>
          <option value="">{t('consultations.goal')}</option>
          {(options.goals || []).map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </SelectInput>
        <TextArea
          id="question"
          label={t('consultations.question')}
          maxLength={500}
          value={form.question}
          onChange={(event) => update('question', event.target.value)}
          hint={t('support.counter', { count: form.question.length, max: 500 })}
          required
        />
        <SelectInput id="period" label={t('consultations.period')} value={form.preferred_period} onChange={(event) => update('preferred_period', event.target.value)} required>
          <option value="">{t('consultations.period')}</option>
          {(options.preferred_periods || []).map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </SelectInput>
        <SelectInput id="method" label={t('consultations.method')} value={form.communication_method} onChange={(event) => update('communication_method', event.target.value)}>
          {communicationMethods.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </SelectInput>
        <SelectInput id="attempt" label={t('consultations.attempt')} value={form.attempt_id} onChange={(event) => update('attempt_id', event.target.value)} required>
          <option value="">{t('consultations.attempt')}</option>
          {attempts.map((item) => (
            <option key={item.attempt_id} value={item.attempt_id}>
              {item.profile_code} · {item.completed_at}
            </option>
          ))}
        </SelectInput>
        {selectedAttempt?.top_patterns?.length ? (
          <div className="chip-row" aria-label={t('results.patterns')}>
            {(selectedAttempt.top_patterns || []).map((pattern) => (
              <PatternChip key={pattern.id || pattern.short_code} pattern={pattern} locale={locale} />
            ))}
          </div>
        ) : null}
        <label className="check-row">
          <input
            type="checkbox"
            checked={form.share_report_consent}
            onChange={(event) => update('share_report_consent', event.target.checked)}
          />
          <span>{t('consultations.consent')}</span>
        </label>
        <p className="muted">{t('consultations.consentHint')}</p>
        {review ? (
          <Card data-testid="consultation-review">
            <h2>{t('consultations.review')}</h2>
            <dl className="detail-list">
              <div><dt>{t('consultations.goal')}</dt><dd>{optionLabel(options.goals, form.goal)}</dd></div>
              <div><dt>{t('consultations.question')}</dt><dd>{form.question}</dd></div>
              <div><dt>{t('consultations.period')}</dt><dd>{optionLabel(options.preferred_periods, form.preferred_period)}</dd></div>
              <div><dt>{t('consultations.method')}</dt><dd>{optionLabel(communicationMethods, form.communication_method || DEFAULT_COMMUNICATION_METHOD)}</dd></div>
              <div>
                <dt>{t('consultations.attempt')}</dt>
                <dd>{selectedAttempt ? `${selectedAttempt.profile_code} · ${selectedAttempt.completed_at}` : '—'}</dd>
              </div>
              <div>
                <dt>{t('consultations.consent')}</dt>
                <dd>{form.share_report_consent ? t('consultations.consentYes') : t('consultations.consentNo')}</dd>
              </div>
            </dl>
          </Card>
        ) : null}
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        <div className="action-row">
          {review ? <Button type="button" tone="ghost" onClick={() => setReview(false)}>{t('actions.back')}</Button> : null}
          <Button type="submit" loading={submitting} disabled={!canSubmit || submitting}>
            {review ? t('actions.submit') : t('consultations.review')}
          </Button>
        </div>
      </form>
      <Card>
        <h2>{t('consultations.newTitle')}</h2>
        <p className="muted">{t('consultations.assignHint')}</p>
        <p className="muted">{t('consultations.consentHint')}</p>
      </Card>
    </div>
  );
}
